import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api, endpoints } from '../../config/api';
import { Button, Alert, Spinner } from '../../components/ui';
import { useT, apiErrorMessage } from '../../i18n';
import './AcceptInvitePage.css';

const messages = {
  not_found: { tr: 'Bu davet linki geçersiz.', en: 'This invitation link is invalid.' },
  expired: { tr: 'Bu davetin süresi dolmuş. Yönetiminizden yeni bir davet istemeniz gerekiyor.', en: 'This invitation has expired. Ask your administrator for a new one.' },
  revoked: { tr: 'Bu davet iptal edilmiş.', en: 'This invitation has been revoked.' },
  already_accepted: { tr: 'Bu davet zaten kabul edilmiş. Giriş yapmayı deneyin.', en: 'This invitation has already been accepted. Try signing in.' },
  msError: { tr: 'Microsoft ile devam edilemedi', en: 'Couldn’t continue with Microsoft' },
  notFoundTitle: { tr: 'Davet Bulunamadı', en: 'Invitation Not Found' },
  backToLogin: { tr: 'Giriş sayfasına dön', en: 'Back to sign-in' },
  title: { tr: 'Ekip Davetiniz', en: 'Your Team Invitation' },
  anAdmin: { tr: 'Bir yönetici', en: 'An administrator' },
  invited: {
    tr: ({ inviter, org, role }) => <><strong>{inviter}</strong>, sizi{' '}<strong>{org}</strong> organizasyonuna{' '}<strong>{role}</strong> olarak davet etti.</>,
    en: ({ inviter, org, role }) => <><strong>{inviter}</strong> invited you to join{' '}<strong>{org}</strong> as{' '}<strong>{role}</strong>.</>,
  },
  role_org_admin: { tr: 'Organizasyon Admin', en: 'an Organization Admin' },
  role_org_user: { tr: 'Kullanıcı', en: 'a User' },
  redirecting: { tr: 'Yönlendiriliyor...', en: 'Redirecting...' },
  accept: { tr: 'Microsoft ile Daveti Kabul Et', en: 'Accept with Microsoft' },
  hint: {
    tr: ({ email }) => <>Daveti kabul etmek için <strong>{email}</strong> adresine ait Microsoft hesabıyla giriş yapmalısınız. Şifre belirlemeniz gerekmez.</>,
    en: ({ email }) => <>Sign in with the Microsoft account for <strong>{email}</strong>. No password needed.</>,
  },
};

export const AcceptInvitePage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [invalidReason, setInvalidReason] = useState(null);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [msRedirecting, setMsRedirecting] = useState(false);
  const { t } = useT(messages);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(endpoints.invites.lookup(token));
        if (!cancelled) setInvite(data);
      } catch (err) {
        if (!cancelled) setInvalidReason(err.response?.data?.reason || 'not_found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Accepting happens through Microsoft only: the invite token rides through
  // the signed OAuth state (organizations/sso_login.py::_resolve_invite_login)
  // and the backend requires the Microsoft account's email to match the
  // invited one exactly — so this works even for people outside the
  // organization's own domains. The account is created there, at sign-in.
  const handleMicrosoftAccept = async () => {
    setError('');
    setMsRedirecting(true);
    try {
      const res = await api.get(endpoints.auth.ssoMicrosoftLoginUrl, { params: { invite_token: token } });
      window.location.href = res.authorize_url;
    } catch (err) {
      setError(apiErrorMessage(err, messages.msError));
      setMsRedirecting(false);
    }
  };

  return (
    <div className="invite-page">
      <div className="invite-card">
        {loading ? (
          <div className="invite-loading">
            <Spinner />
          </div>
        ) : invalidReason ? (
          <>
            <div className="register-header">
              <h1>{t('notFoundTitle')}</h1>
            </div>
            <Alert variant="error">{t(['expired', 'revoked', 'already_accepted'].includes(invalidReason) ? invalidReason : 'not_found')}</Alert>
            <div className="invite-back-link">
              <a href="/login">{t('backToLogin')}</a>
            </div>
          </>
        ) : (
          <>
            <div className="register-header">
              <h1>{t('title')}</h1>
              <p>
                {t('invited', {
                  inviter: invite.inviter_name || t('anAdmin'),
                  org: invite.organization_name,
                  role: messages[`role_${invite.role}`] ? t(`role_${invite.role}`) : invite.role_display,
                })}
              </p>
            </div>

            {error && <Alert variant="error">{error}</Alert>}

            <Button
              type="button"
              variant="microsoft"
              className="invite-microsoft-btn"
              onClick={handleMicrosoftAccept}
              disabled={msRedirecting}
            >
              <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect width="11" height="11" fill="#f25022" />
                <rect x="12" width="11" height="11" fill="#7fba00" />
                <rect y="12" width="11" height="11" fill="#00a4ef" />
                <rect x="12" y="12" width="11" height="11" fill="#ffb900" />
              </svg>
              {msRedirecting ? t('redirecting') : t('accept')}
            </Button>
            <p className="invite-microsoft-hint">{t('hint', { email: invite.email })}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitePage;
